import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { RequireAdmin, RequireAuth, RedirectAuthenticated } from './components/RouteGuards';
import { HubShell } from './components/HubShell';
import { LoginPage } from './pages/auth/LoginPage';
import { SignupPage } from './pages/auth/SignupPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { ClaimAccessPage } from './pages/auth/ClaimAccessPage';
import { AccessExpiredPage, PurchasePendingPage } from './pages/auth/StatusPage';
import { OnboardingPage } from './pages/hub/OnboardingPage';
import { DashboardPage } from './pages/hub/DashboardPage';
import { RebuildsPage } from './pages/hub/RebuildsPage';
import { VolumeOverviewPage } from './pages/hub/VolumeOverviewPage';
import { ProfilePage } from './pages/hub/ProfilePage';
import { BlueprintPage, CallsPage, CommunityPage, DelphiPage, LibraryPage, RebuildMapPage, ScorecardPage, WorkbookPage } from './pages/hub/PhasePages';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminMembersPage } from './pages/admin/AdminMembersPage';
import { AdminEntitlementsPage } from './pages/admin/AdminEntitlementsPage';
import { AdminVolumesPage } from './pages/admin/AdminVolumesPage';
import { AdminProductsPage } from './pages/admin/AdminProductsPage';


const CoursePlayerPage = lazy(() => import('./pages/hub/CoursePlayerPage').then((module) => ({ default: module.CoursePlayerPage })));
const AdminCoursePage = lazy(() => import('./pages/admin/AdminCoursePage').then((module) => ({ default: module.AdminCoursePage })));
const lazyPage = (page: React.ReactNode) => <Suspense fallback={<div className="page-loading"><span className="loader" />Loading workspace…</div>}>{page}</Suspense>;

const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/hub" replace /> },
  {
    element: <RedirectAuthenticated />,
    children: [
      { path: '/hub/login', element: <LoginPage /> },
      { path: '/hub/signup', element: <SignupPage /> },
      { path: '/hub/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  { path: '/hub/reset-password', element: <ResetPasswordPage /> },
  { path: '/hub/purchase-pending', element: <PurchasePendingPage /> },
  { path: '/hub/access-expired', element: <AccessExpiredPage /> },
  {
    element: <RequireAuth />,
    children: [
      { path: '/hub/onboarding', element: <OnboardingPage /> },
      { path: '/hub/claim-access', element: <ClaimAccessPage /> },
      {
        element: <HubShell />,
        children: [
          { path: '/hub', element: <DashboardPage /> },
          { path: '/hub/rebuilds', element: <RebuildsPage /> },
          { path: '/hub/rebuilds/:volumeSlug', element: <VolumeOverviewPage /> },
          { path: '/hub/rebuilds/:volumeSlug/course', element: lazyPage(<CoursePlayerPage />) },
          { path: '/hub/rebuilds/:volumeSlug/course/:moduleSlug/:itemSlug', element: lazyPage(<CoursePlayerPage />) },
          { path: '/hub/workbook', element: <WorkbookPage /> },
          { path: '/hub/blueprint', element: <BlueprintPage /> },
          { path: '/hub/scorecard', element: <ScorecardPage /> },
          { path: '/hub/rebuild-map', element: <RebuildMapPage /> },
          { path: '/hub/delphi', element: <DelphiPage /> },
          { path: '/hub/calls', element: <CallsPage /> },
          { path: '/hub/community', element: <CommunityPage /> },
          { path: '/hub/library', element: <LibraryPage /> },
          { path: '/hub/profile', element: <ProfilePage /> },
          {
            element: <RequireAdmin />,
            children: [
              { path: '/hub/admin', element: <AdminDashboardPage /> },
              { path: '/hub/admin/members', element: <AdminMembersPage /> },
              { path: '/hub/admin/entitlements', element: <AdminEntitlementsPage /> },
              { path: '/hub/admin/volumes', element: <AdminVolumesPage /> },
              { path: '/hub/admin/products', element: <AdminProductsPage /> },
              { path: '/hub/admin/course', element: lazyPage(<AdminCoursePage />) },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/hub" replace /> },
]);

export default function App() {
  return <AuthProvider><RouterProvider router={router} /></AuthProvider>;
}
